/**
 * A player's match reports, fetched and shaped (SE-6986).
 *
 * The clock is read HERE, once per mount, and handed into the pure layer. The
 * row/staleness maths refuses to read time itself so that a test can pin the
 * instant — and so the flag cannot flip mid-render because two calls read the
 * clock microseconds apart.
 * @module features/player-match-reports/hooks/usePlayerMatchReports
 */

import { useEffect, useMemo, useState } from "react";

import { usePlayerMatchReportsQuery } from "@/generated/graphql";

import {
  toPlayerReportRows,
  toPlayerReportStats,
} from "../utils/toPlayerReportRows";

import type {
  PlayerReportRow,
  PlayerReportStats,
} from "../utils/toPlayerReportRows";

/**
 * Enough for any real player. A player carrying more than 100 match reports is
 * not a browsing surface any more, and the section says so rather than paging
 * silently — `hasMore` is exposed for exactly that.
 */
const PAGE_SIZE = 100;

/** What the section renders. */
export interface UsePlayerMatchReportsResult {
  /** One row per fixture the player was watched in, newest first. */
  readonly rows: readonly PlayerReportRow[];
  /** The aggregation cards. */
  readonly stats: PlayerReportStats;
  /** True while loading. */
  readonly isLoading: boolean;
  /** A failure, or null. */
  readonly error: string | null;
  /** True when more reports exist than one page could carry. */
  readonly hasMore: boolean;
}

/**
 * Fetches and shapes a player's match reports.
 * @param playerId - The player whose page this is. Empty skips the query.
 * @returns Rows, stats and load state.
 */
const usePlayerMatchReports = (
  playerId: string | undefined
): UsePlayerMatchReportsResult => {
  // One instant per mount, held in LAZY state: the initializer runs once, the
  // value never changes, and — unlike a ref — reading it during render is
  // sanctioned. A bare `new Date()` in render would be impure and could flip a
  // staleness boundary between two renders of the same mount.
  const [now] = useState(() => new Date());

  const { data, loading, error, fetchMore } = usePlayerMatchReportsQuery({
    variables: {
      filter: { playerIds: [playerId ?? ""], submittedOnly: true },
      first: PAGE_SIZE,
    },
    // The same guard the rest of the player page uses (SE-5638): the route
    // param is momentarily undefined/"" while the route mounts, and an empty
    // id is a request the server rejects rather than an empty result.
    skip: !playerId?.trim(),
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  const rows = useMemo(
    () =>
      toPlayerReportRows(
        data?.listMatchReports.edges.map(edge => edge.node) ?? [],
        playerId ?? "",
        now
      ),
    [data, now, playerId]
  );

  // Page to completion: the stats are averages over the CAREER, and an
  // average over one page of a multi-page career is not a smaller average —
  // it is a wrong one that looks right.
  const pageInfo = data?.listMatchReports.pageInfo;
  useEffect(() => {
    if (pageInfo?.hasNextPage === true && !loading) {
      void fetchMore({ variables: { after: pageInfo.endCursor } });
    }
  }, [fetchMore, loading, pageInfo]);

  return {
    rows,
    stats: useMemo(() => toPlayerReportStats(rows), [rows]),
    isLoading: loading && data === undefined,
    error: error?.message ?? null,
    hasMore: pageInfo?.hasNextPage ?? false,
  };
};

export default usePlayerMatchReports;
